/**
 * 認證相關 API 呼叫 + TanStack Query hooks。
 * 型別盡量複用 @bom/shared 的 response schema 型別。
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { SwitchTenantResponse, Role } from '@bom/shared';
import { api } from '../lib/apiClient';

/**
 * 實際 login 回應：platform_admin 拿 `platform_jwt`，一般 user 拿 `session_jwt`
 * （見 auth-lambda/src/login.ts:94）。兩者皆為「會話級」token，用法相同。
 * shared 的 LoginResponseSchema 只描述 session_jwt 情形，故此處用本地型別涵蓋兩者。
 */
export interface LoginResponse {
  session_jwt?: string;
  platform_jwt?: string;
  expires_at: number;
  user: {
    id: string;
    email: string;
    name: string;
    is_platform_admin: boolean;
    must_change_password: boolean;
  };
}

/**
 * /me 回應。tenants 项目已 join 产品 META，含 name/code（见 auth-lambda/src/me.ts）。
 */
export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    is_platform_admin: boolean;
    status: string;
    must_change_password: boolean;
    last_login_at?: number;
  };
  tenants: Array<{
    tenant_id: string;
    name: string;
    code: string;
    role: Role;
    status: string;
    added_at?: number;
  }>;
  current_jwt_type?: string;
  current_tenant_id?: string;
  current_role?: Role;
}

export function login(email: string, password: string): Promise<LoginResponse> {
  return api.post<LoginResponse>('/auth/login', { email, password }, { auth: 'none' });
}

export function switchTenant(tenantId: string): Promise<SwitchTenantResponse> {
  return api.post<SwitchTenantResponse>(
    '/auth/switch-tenant',
    { tenant_id: tenantId },
    { auth: 'session' },
  );
}

export function changePassword(current_password: string, new_password: string): Promise<void> {
  return api.post<void>('/auth/change-password', { current_password, new_password });
}

export function fetchMe(): Promise<MeResponse> {
  return api.get<MeResponse>('/me', { auth: 'session' });
}

export function useMe(enabled = true) {
  return useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled });
}

export function useLogin() {
  return useMutation({
    mutationFn: (vars: { email: string; password: string }) => login(vars.email, vars.password),
  });
}

export function useSwitchTenant() {
  return useMutation({ mutationFn: (tenantId: string) => switchTenant(tenantId) });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (vars: { current_password: string; new_password: string }) =>
      changePassword(vars.current_password, vars.new_password),
  });
}
